import { faChevronsLeft, faChevronsRight } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Tooltip, Typography } from "@mui/material"
import { ITranslation } from "app/language/model"
import { translate } from "app/language/service"
import { getTranslationForCurrentLocale } from "app/language/tools/common"
import { IRootState } from "app/model"
import { NotificationSlider } from "notification/components/NotificationSlider"
import { IPage } from "page/model"
import React from "react"
import { connect } from "react-redux"
import { IMenuGroup, IMenuItem, MENU_CLOSED_WIDTH_IN_REM, MENU_OPENED_WIDTH_IN_REM } from "ui/model"
import { setMenuExpansion } from "ui/tools/menu"
import { getLogoMessageFromOptions } from "ui/tools/options"
import { UserMenuPanel } from "user/components/UserMenuPanel"
import { FlexBox } from "../FlexBox"
import { Logo } from "../Logo"
import { MenuItem } from "./MenuItem"

interface IMenuDesktopProps {
  isMenuOpened: boolean
  isNotificationSliderOpened: boolean
  menus: IMenuItem[]
  activePage: IPage
  menuTitleTranslation: ITranslation
}

export const MenuDesktopFn = React.memo(
  ({ isMenuOpened, isNotificationSliderOpened, menus, activePage, menuTitleTranslation }: IMenuDesktopProps) => {
    const isOpened = isMenuOpened && !isNotificationSliderOpened
    return (
      <>
        <FlexBox
          fullHeight
          vertical
          jc="flex-start"
          sx={{
            position: "fixed",
            zIndex: 11,
            left: 0,
            top: 0,
            width: isOpened ? MENU_OPENED_WIDTH_IN_REM : MENU_CLOSED_WIDTH_IN_REM,
            minWidth: isOpened ? MENU_OPENED_WIDTH_IN_REM : MENU_CLOSED_WIDTH_IN_REM,
            maxWidth: isOpened ? MENU_OPENED_WIDTH_IN_REM : MENU_CLOSED_WIDTH_IN_REM,
            backgroundColor: theme => theme.palette.background.paper,
            overflow: "hidden",
            transition: "all 600ms ease"
          }}>
          <Logo
            sx={{
              mt: "1rem",
              mb: ".4rem",
              ml: isOpened ? "-1.2rem" : undefined,
              width: isOpened ? "16rem" : "4rem",
              height: "5rem",
              minHeight: "5rem",
              marginTop: isOpened ? "1rem" : 0,
              transition: "all 300ms ease"
            }}
            useTrunkedImage={!isOpened}
          />
          {isOpened && (
            <Typography
              id="menu-title"
              color="textPrimary"
              sx={{
                width: "calc(100% - 2rem)",
                minWidth: isOpened
                  ? `calc(${MENU_OPENED_WIDTH_IN_REM} - 2rem)`
                  : `calc(${MENU_CLOSED_WIDTH_IN_REM} - 2rem)`,
                height: "2.75rem",
                minHeight: "2.75rem",
                maxHeight: "2.75rem",
                textAlign: "center",
                fontSize: ".9rem",
                marginBottom: "1rem",
                animation: "fadeInTopDown 600ms"
              }}>
              {getLogoMessageFromOptions() || getTranslationForCurrentLocale(menuTitleTranslation)}
            </Typography>
          )}
          <Box
            sx={{
              width: "calc(100% - 2rem)",
              minWidth: isOpened
                ? `calc(${MENU_OPENED_WIDTH_IN_REM} - 2rem)`
                : `calc(${MENU_CLOSED_WIDTH_IN_REM} - 2rem)`,
              height: "calc(100% - 16.5rem)",
              overflow: "auto"
            }}>
            {menus.map((menu, index) => (
              <React.Fragment key={index}>
                <MenuItem
                  key={`group-${index}`}
                  menu={menu}
                  isOpened={isOpened}
                  isActive={menu.pagePaths.includes(activePage.path)}
                  isFirst={index === 0}
                />
                {(menu as IMenuGroup).items
                  ? (menu as IMenuGroup).items.map((subMenu, subIndex) => (
                      <MenuItem
                        key={`${index}-${subIndex}`}
                        menu={subMenu}
                        isOpened={isOpened}
                        isActive={subMenu.pagePaths.includes(activePage.path)}
                        isFirst={index === 0}
                      />
                    ))
                  : null}
              </React.Fragment>
            ))}
          </Box>
          <UserMenuPanel sx={{ position: "absolute", bottom: 0 }} />
        </FlexBox>
        <NotificationSlider />
        {!isNotificationSliderOpened && (
          <Tooltip title={translate(`menu.${isOpened ? "close" : "open"}`)}>
            <Box
              id="menu-toggle-button"
              sx={{
                "position": "fixed",
                "zIndex": "2",
                "top": 0,
                "left": isOpened ? MENU_OPENED_WIDTH_IN_REM : MENU_CLOSED_WIDTH_IN_REM,
                "borderLeft": theme => `1px solid ${theme.palette.background.default}`,
                "width": "2rem !important",
                "height": "calc(4rem - 1px)",
                "display": "flex",
                "alignItems": "center",
                "justifyContent": "center",
                "cursor": "pointer",
                "backgroundColor": theme => theme.palette.background.paper,
                "transition": "all 600ms ease",
                "& .menu-close": {
                  color: theme => theme.palette.text.primary
                }
              }}
              onClick={() => setMenuExpansion(!isOpened)}>
              <FontAwesomeIcon icon={isOpened ? faChevronsLeft : faChevronsRight} size="lg" className="menu-close" />
            </Box>
          </Tooltip>
        )}
      </>
    )
  }
)

export const MenuDesktop = connect(
  (state: IRootState): IMenuDesktopProps => ({
    isMenuOpened: state.ui.isMenuOpened,
    isNotificationSliderOpened: state.notification.isSliderOpened,
    activePage: state.page.activePage,
    menus: state.ui.menus,
    menuTitleTranslation: state.ui.config.translations.menuTitle
  })
)(MenuDesktopFn)
